import { useLocation, useSearchParams } from '@solidjs/router';
import { gql } from 'graphql-request';
import { createEffect, createSignal, on, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { ErrorCatcher } from '../../tools/error-catcher';
import headerStore from '../../stores/header-store';
import { SeoSettings } from '../seo-settings/seo-settings';
import { PageCacheType } from '../../types/shared';
import { SearchAutocomplete } from './search-autocomplete';
import { SEARCH_RESULTS_LIST_ANCHOR, SearchResults } from './search-results';
import {
    createSearchClient,
    isSearchResultFilter,
    SEARCH_PAGE_PARAM,
    SEARCH_QUERY_PARAM,
    SEARCH_TYPE_PARAM,
    SearchHit,
    SearchResultFilter,
} from './search-shared';

export const GET_PAGE_QUERY = gql`
    query GetPage($key: String!) {
        page: resource(key: $key) {
            content
            key
        }
    }
`;

const HITS_PER_PAGE = 10;
/* The product row is a slider, so it can hold more than one page shows. */
const PRODUCT_ROW_LIMIT = 8;

/* Breathing room between the header's bottom edge and the tabs after paging. */
const SCROLL_GAP_PX = 24;
/* The header's expand transition is 300ms (header.tsx); wait it out before measuring. */
const HEADER_EXPAND_MS = 350;

type ResultState = {
    hits: SearchHit[];
    totalHits: number;
    totalPages: number;
};

const EMPTY_RESULTS: ResultState = { hits: [], totalHits: 0, totalPages: 0 };

/* The search page (Figma 1602:11181 desktop, 1436:10395 mobile). The URL is the state:
   `?s=` is the query, `?type=` the content-type tab, `?page=` the 1-based page — so
   back/forward, reloads and shared links all land on the same results. Every change of
   those three runs one Algolia request holding three queries: the page of hits for the
   active tab, the per-type counts for the tab labels, and the products for the card row. */
export const Search = () => {
    const location = useLocation();

    const {
        localize,
        createCachedResource,
        algoliaSettings,
        siteInfo,
        setCacheType,
        appStore,
        viewport,
        headerSettings,
    } = useContext(AppContext);

    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    const showHcpHeader = () => headerSettings.activateHcpHeader && isHcp() && !isOriginalTablet();

    const [pageContent] = createCachedResource(GET_PAGE_QUERY, () => ({ key: location.pathname }), true);

    const { searchAppId, searchApiClientKey } = algoliaSettings;
    if (!searchAppId || !searchApiClientKey) {
        return <div>No searchAppId / searchApiClientKey was found..</div>;
    }

    const { siteId, target } = siteInfo;
    if (!siteId || !target) {
        return <div>No siteId / target was found..</div>;
    }

    const [searchParams, setSearchParams] = useSearchParams();

    const query = () => (searchParams[SEARCH_QUERY_PARAM] ?? '').toString().trim();
    const filter = (): SearchResultFilter => {
        const raw = searchParams[SEARCH_TYPE_PARAM];
        return isSearchResultFilter(raw) ? raw : 'all';
    };
    const page = () => {
        const parsed = parseInt(searchParams[SEARCH_PAGE_PARAM] ?? '', 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    };

    /* What is in the field; only becomes the query on submit. */
    const [draft, setDraft] = createSignal(query());
    const [results, setResults] = createSignal<ResultState>(EMPTY_RESULTS);
    const [counts, setCounts] = createSignal<Partial<Record<SearchResultFilter, number>>>({});
    const [productHits, setProductHits] = createSignal<SearchHit[]>([]);
    const [loading, setLoading] = createSignal(false);

    const isHcp = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }

        return Boolean(pageContent()?.page?.content.isHealthcareProfessional);
    };

    const setHcp = () => {
        appStore.setHcp(checkHcp());
        return null;
    };

    setHcp();

    createEffect(() => {
        setHcp();
    });

    const { client, indexName } = createSearchClient({ searchAppId, searchApiClientKey }, { siteId, target });

    /* Responses can land out of order; only the newest request may write state. */
    let latestRequest = 0;

    const runSearch = async () => {
        const requestId = ++latestRequest;
        const text = query();

        if (!text) {
            setResults(EMPTY_RESULTS);
            setCounts({});
            setProductHits([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const { results: responses } = await client.search<SearchHit>([
                {
                    indexName,
                    query: text,
                    params: {
                        hitsPerPage: HITS_PER_PAGE,
                        page: page() - 1,
                        ...(filter() !== 'all' ? { filters: `type:${filter()}` } : {}),
                    },
                },
                {
                    indexName,
                    query: text,
                    params: { hitsPerPage: 0, facets: ['type'] },
                },
                {
                    indexName,
                    query: text,
                    params: { hitsPerPage: PRODUCT_ROW_LIMIT, filters: 'type:product' },
                },
            ]);

            if (requestId !== latestRequest) {
                return;
            }

            const [pageResponse, countsResponse, productsResponse] = responses;

            setResults({
                hits: pageResponse.hits,
                totalHits: pageResponse.nbHits,
                totalPages: pageResponse.nbPages,
            });
            setCounts({
                all: countsResponse.nbHits,
                ...(countsResponse.facets?.type ?? {}),
            });
            setProductHits(productsResponse.hits);
        } catch (error) {
            console.error('error fetching algoliasearch', error);
        } finally {
            if (requestId === latestRequest) {
                setLoading(false);
            }
        }
    };

    /* Effects don't run during SSR, so Algolia is only ever called from the browser. */
    createEffect(on([query, filter, page], () => {
        setDraft(query());
        runSearch();
    }));

    /* Paging lands the tabs row just under the header. The header publishes its rendered
       height as `--header-height`, but it collapses on the way down and re-expands as soon as
       we scroll up, so a target measured against the collapsed header ends up under the
       expanded one. Expand it first, let the transition finish, then measure and scroll. */
    const scrollToResults = () => {
        const { floatHeader, setFloatHeader } = headerStore;

        const scroll = () => {
            const target = document.getElementById(SEARCH_RESULTS_LIST_ANCHOR);

            if (!target) {
                return;
            }

            const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
            const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - SCROLL_GAP_PX;

            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        };

        if (floatHeader()) {
            setFloatHeader(false);
            setTimeout(scroll, HEADER_EXPAND_MS);
            return;
        }

        scroll();
    };

    const submit = (value: string) => {
        const trimmed = value.trim();

        if (trimmed === query()) {
            return;
        }

        /* A new query starts over: first page, every type. */
        setSearchParams({
            [SEARCH_QUERY_PARAM]: trimmed,
            [SEARCH_TYPE_PARAM]: undefined,
            [SEARCH_PAGE_PARAM]: undefined,
        });
    };

    const changeFilter = (next: SearchResultFilter) => {
        setSearchParams({
            [SEARCH_TYPE_PARAM]: next === 'all' ? undefined : next,
            [SEARCH_PAGE_PARAM]: undefined,
        });
    };

    const changePage = (next: number) => {
        setSearchParams({ [SEARCH_PAGE_PARAM]: next <= 1 ? undefined : next });
        scrollToResults();
    };

    return (
        <ErrorCatcher componentName="Search">
            <Show when={pageContent()?.page?.content?.seo}>
                <SeoSettings
                    seo={pageContent().page?.content?.seo}
                    title={pageContent().page?.content?.post_title}
                />
            </Show>

            <div id="search" class={clsx('[container-type:inline-size]', showHcpHeader() && 'mt-[6rem] desktop:mt-[8rem]')}>
                {/* Query band: brand wash across the page, heading and the field inside the content column. */}
                <div class="bg-cvi-bg-brand-card">
                    <Section
                        templateShorthand={[12]}
                        widthType="bgFull"
                        removeSpacingBlock={true}
                        customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                    >
                        <div class="flex flex-col gap-cvi-6 py-cvi-9">
                            <h1 class="m-0 heading-xl font-cvi-regular text-cvi-fg-default">
                                <Show when={query()} fallback={localize('search', 'Search')}>
                                    {localize('showing-results-for', 'Showing results for')}
                                </Show>
                            </h1>
                            <SearchAutocomplete
                                id="search-page"
                                class="max-w-cvi-search-field"
                                value={draft()}
                                onInput={setDraft}
                                onSubmit={submit}
                                placeholder={localize('write-a-keyword', 'Write a keyword for what you are looking for…')}
                            />
                        </div>
                    </Section>
                </div>

                <SearchResults
                    query={query()}
                    filter={filter()}
                    page={page()}
                    hitsPerPage={HITS_PER_PAGE}
                    hits={results().hits}
                    totalHits={results().totalHits}
                    totalPages={results().totalPages}
                    counts={counts()}
                    productHits={productHits()}
                    loading={loading()}
                    onFilterChange={changeFilter}
                    onPageChange={changePage}
                />
            </div>
        </ErrorCatcher>
    );
};
